import React, { useState, useRef } from 'react';
import './IngredientLibrary.css';
import CameraCapture from './CameraCapture';

const API_URL = 'https://iyu787mcdb.execute-api.us-east-1.amazonaws.com/prod';

interface Ingredient {
  id: string;
  name: string;
  category: string;
  imageUrl?: string;
  co2ePerKg?: number;
  emissionsData?: any;
  createdAt?: string;
  modifiedAt?: string;
}

interface IngredientLibraryProps {
  readOnly?: boolean;
}

const IngredientLibrary: React.FC<IngredientLibraryProps> = ({ readOnly = false }) => {
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    co2ePerKg: '',
    emissionsData: null as any
  });

  const [editingId, setEditingId] = useState<string | null>(null);

  // Load ingredients on mount
  React.useEffect(() => {
    loadIngredients();
  }, []);

  const loadIngredients = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/ingredient-storage`);
      const data = await response.json();
      setIngredients(data);
    } catch (error) {
      console.error('Failed to load ingredients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageData = reader.result as string;
        setPreviewUrl(imageData);
        analyzeImage(imageData);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = (imageData: string) => {
    setPreviewUrl(imageData);
    setShowCamera(false);
    analyzeImage(imageData);
  };

  const analyzeImage = async (imageData: string) => {
    setIsAnalyzing(true);
    try {
      const response = await fetch(`${API_URL}/ingredients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: imageData })
      });

      const result = await response.json();
      
      if (result.error) {
        alert('Failed to analyze image: ' + result.error);
        return;
      }
      
      // Auto-populate ALL fields from AI
      const emissions = result.emissions || {};
      const totalGrams = (emissions.scope1?.value || 0) + 
                        (emissions.scope2?.value || 0) + 
                        (emissions.scope3?.value || 0);
      const totalKg = totalGrams / 1000;
      
      setFormData({
        name: result.name || 'Unknown Product',
        category: result.category || 'other',
        co2ePerKg: totalKg.toFixed(2),
        emissionsData: result
      });
      
      setShowAddForm(true);
    } catch (error) {
      console.error('Error analyzing image:', error);
      alert('Failed to analyze image. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const payload: any = {
        name: formData.name,
        category: formData.category,
        co2ePerKg: parseFloat(formData.co2ePerKg),
        emissionsData: formData.emissionsData,
        image: previewUrl
      };
      
      // Include id and other fields when updating
      if (editingId) {
        const existingIngredient = ingredients.find(i => i.id === editingId);
        payload.id = editingId;
        payload.imageUrl = existingIngredient?.imageUrl;
        payload.createdAt = existingIngredient?.createdAt;
      }
      
      // Save to S3
      const response = await fetch(`${API_URL}/ingredient-storage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const savedIngredient = await response.json();
      
      if (editingId) {
        // Update existing
        setIngredients(ingredients.map(ing => 
          ing.id === editingId ? savedIngredient : ing
        ));
        setEditingId(null);
      } else {
        // Add new
        setIngredients([...ingredients, savedIngredient]);
      }
    } catch (error) {
      alert('Failed to save ingredient: ' + error);
      setIsSaving(false);
      return;
    }
    
    setIsSaving(false);
    
    // Reset form
    setFormData({ name: '', category: '', co2ePerKg: '', emissionsData: null });
    setSelectedImage(null);
    setPreviewUrl('');
    setShowAddForm(false);
  };

  const handleEdit = (ingredient: Ingredient) => {
    setEditingId(ingredient.id);
    setFormData({
      name: ingredient.name,
      category: ingredient.category,
      co2ePerKg: ingredient.co2ePerKg?.toString() || '',
      emissionsData: ingredient.emissionsData
    });
    setPreviewUrl(ingredient.imageUrl || '');
    setShowAddForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Delete this ingredient?')) {
      try {
        const response = await fetch(`${API_URL}/ingredient-storage?id=${id}`, {
          method: 'DELETE'
        });
        
        if (!response.ok) throw new Error('Failed to delete');
        
        setIngredients(ingredients.filter(ing => ing.id !== id));
      } catch (error) {
        alert('Failed to delete ingredient: ' + error);
      }
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingId(null);
    setFormData({ name: '', category: '', co2ePerKg: '', emissionsData: null });
    setPreviewUrl('');
    setSelectedImage(null);
  };

  return (
    <div className="ingredient-library">
      <div className="library-header">
        <h2>Ingredient Library</h2>
        {!readOnly && (
          <div className="header-actions">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
            />
            <button 
              className="btn-secondary"
              onClick={() => fileInputRef.current?.click()}
            >
              📁 Upload Photo
            </button>
            <button 
              className="btn-primary"
              onClick={() => setShowCamera(true)}
            >
              📷 Take Photo
            </button>
          </div>
        )}
      </div>

      {showAddForm && (
        <div className="add-ingredient-form">
          <h3>{editingId ? 'Edit Ingredient' : 'Review & Save'}</h3>
          <form onSubmit={handleSubmit}>
            {previewUrl && (
              <div className="form-group">
                <label>Photo</label>
                <div className="image-preview-large">
                  <img src={previewUrl} alt="Ingredient" />
                  <button 
                    type="button" 
                    className="btn-secondary"
                    onClick={() => setShowCamera(true)}
                  >
                    📷 Retake Photo
                  </button>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Ingredient Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  required
                >
                  <option value="">Select category</option>
                  <option value="chocolate">Chocolate</option>
                  <option value="dairy">Dairy</option>
                  <option value="sugar">Sugar & Sweeteners</option>
                  <option value="nuts">Nuts & Seeds</option>
                  <option value="flavoring">Flavorings</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>CO2e per kg</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.co2ePerKg}
                  onChange={(e) => setFormData({...formData, co2ePerKg: e.target.value})}
                  required
                />
              </div>

            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={isSaving}>
                {isSaving ? 'Saving...' : editingId ? 'Update' : 'Save'} Ingredient
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="ingredients-grid">
        {isLoading ? (
          <div className="empty-state">
            <p>Loading ingredients...</p>
          </div>
        ) : ingredients.length === 0 ? (
          <div className="empty-state">
            <p>No ingredients yet. Add your first ingredient to get started!</p>
          </div>
        ) : (
          ingredients.map(ingredient => (
            <div key={ingredient.id} className="ingredient-card">
              {ingredient.imageUrl && (
                <div className="ingredient-image">
                  <img src={ingredient.imageUrl} alt={ingredient.name} />
                </div>
              )}
              <div className="ingredient-info">
                <h3>{ingredient.name}</h3>
                {ingredient.co2ePerKg && (
                  <div className="ingredient-emissions">
                    🌍 {ingredient.co2ePerKg} kg CO₂e/kg
                  </div>
                )}
                {!readOnly && (
                  <div className="ingredient-actions">
                    <button 
                      className="btn-edit"
                      onClick={() => handleEdit(ingredient)}
                    >
                      ✏️ Edit
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDelete(ingredient.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {showCamera && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

export default IngredientLibrary;
